import React, { useEffect, useState } from 'react';
import Chart from 'chart.js/auto';
import { defaults } from 'chart.js';
import { Chart as ChartReact } from 'react-chartjs-2';
import FrequencyButtonGroup from 'components/summary-tab/freq-button-group';
import NoDataAvailable from 'components/empty-state/index';
import useStats from 'state/stats/hooks/useStats';
import { STATS } from 'state/stats/type';
import * as util from 'utils/dashboard';
import CURRENCY from 'utils/currency';
import { Link } from 'react-router-dom';
import { formatToTz } from 'utils/date-format';
import useOrganisation from 'state/organisation/hooks/useOrganisation';
import { getChartOptions, customChartConfig } from './config';
import { verticalAnnotation } from './plugins/tooltipVerticalAnnotation';
import { htmlLegendPlugin } from './plugins/htmlLegend';
import { fillInTheGapsUtil, timeUnitConverter } from 'utils/chart';
import { cn } from 'lib/utils';
import { Icon } from 'components/ui/icon';

interface IProps {
  currentDates: [string, string];
  prevDates: [string, string];
}

const GraphLastUpdatedTimer = () => {
  const { stats } = useStats();
  const { graph, isLoading } = stats;
  const [timer, setTimer] = useState(0);
  const [lastUpdateMessage, setLastUpdateMessage] = useState(
    'Graph updated a few seconds ago'
  );

  useEffect(() => {
    setTimer(0);
  }, [graph]);

  useEffect(() => {
    const second = timer < 60 ? timer : 0;
    const minute = timer >= 60 && timer <= 3540 ? Math.floor(timer / 60) : 0;
    if (second) {
      setLastUpdateMessage('Graph updated a few seconds ago');
    } else if (minute) {
      setLastUpdateMessage(
        `Graph updated ${minute} minute${minute > 1 ? 's' : ''} ago`
      );
    }
    const timerInterval = setInterval(() => {
      setTimer((prevTimer) => prevTimer + 1);
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [timer]);

  return (
    <div className="flex justify-center">
      {!isLoading && (
        <>
          <Icon name="clock" size={18} className="mr-1 text-gray-blue-50" />
          <div className="text-gray-blue-50 text-xs font-normal leading-4">
            {lastUpdateMessage}
          </div>
        </>
      )}
    </div>
  );
};

const Graph = ({ currentDates, prevDates }: IProps): JSX.Element => {
  type IDataset = {
    x: Date[];
    y: number[];
    type: STATS.IPaymentType;
    value: STATS.ValuePoint[];
  };

  defaults.font.family = 'Inter, sans-serif';

  const [dateFrom, dateTo] = currentDates;
  const [prevFrom, prevTo] = prevDates;
  const { stats, toFetchGraphPoints } = useStats();
  const { activeSummaryTab, summary, graph } = stats;
  const { organisation } = useOrganisation();
  const { settings } = organisation;

  const interval = util.intervalInDays(dateFrom, dateTo);
  const intervalTypes = util.mapIntervalToFrequency(interval);
  const [activeFreq, setActiveFreq] = useState(intervalTypes[0]);
  const [datasets, setDatasets] = useState<IDataset[]>([]);

  const getComparisonData = () => {
    const totalSummary = Array.isArray(summary)
      ? summary?.filter(
          (summ) => summ.payment_type === STATS.IPaymentType.ALL
        )?.[0]
      : undefined;
    if (!totalSummary?.current || !totalSummary?.previous) return [0, 0];
    const { current, previous } = totalSummary;

    if (activeSummaryTab === STATS.graphType.payments)
      return [current.payments, previous.payments];
    else if (activeSummaryTab === STATS.graphType.aov)
      return [
        CURRENCY.getCompactNotation(
          current?.payments ? current?.gross_volume / current?.payments : 0
        ),
        CURRENCY.getCompactNotation(
          previous?.payments ? previous?.gross_volume / previous?.payments : 0
        ),
      ];
    else
      return [
        CURRENCY.getCompactNotation(current.gross_volume),
        CURRENCY.getCompactNotation(previous.gross_volume),
      ];
  };

  const SummaryWithComparison = () => {
    const formatString = 'dd MMM yyyy';

    const fromStr = formatToTz(dateFrom, settings?.timezone, formatString);
    const toStr = formatToTz(dateTo, settings?.timezone, formatString);
    const prevFromStr = formatToTz(prevFrom, settings?.timezone, formatString);
    const prevToStr = formatToTz(prevTo, settings?.timezone, formatString);

    const [value, prevValue] = getComparisonData();

    return (
      <div className="[&>*:first-child]:text-gray-blue-70 [&>*:nth-child(2)]:text-gray-blue-50">
        <p data-cy="current-date-summary">
          <span className="mr-2 footnote-medium">{`${fromStr} - ${toStr} :`}</span>
          <span className="body-subtext-semibold">{value}</span>
        </p>
        <p data-cy="prev-date-summary">
          <span className="mr-2 footnote-medium">{`${prevFromStr} - ${prevToStr} :`}</span>
          <span className="body-subtext-semibold">{prevValue}</span>
        </p>
      </div>
    );
  };

  useEffect(() => {
    if (Array.isArray(graph) && graph.length > 0) {
      let datasetsPerType: IDataset[] = [];

      graph.forEach((g: STATS.IPaymentTypeGraph) => {
        const data: {
          x: Date[];
          y: number[];
          value: STATS.ValuePoint[];
        } = {
          x: [],
          y: [],
          value: [],
        };

        g[activeFreq].forEach((point: STATS.IDataPointValue) => {
          data.x.push(new Date(point.date));

          if (
            activeSummaryTab == STATS.graphType.gross_amount &&
            point.value[0] &&
            'gross_amount' in point.value[0]
          ) {
            const value = point.value[0]?.gross_amount || 0;

            data.y.push(value);
          } else if (
            activeSummaryTab == STATS.graphType.payments &&
            point.value[0] &&
            'transaction_count' in point.value[0]
          ) {
            const value = (point.value[0]?.transaction_count || 0) as number;
            data.y.push(value);
          } else if (
            activeSummaryTab == STATS.graphType.aov &&
            point.value[0] &&
            'transaction_count' in point.value[0] &&
            'gross_amount' in point.value[0]
          ) {
            const value = point.value[0]?.transaction_count
              ? point.value[0]?.gross_amount / point.value[0]?.transaction_count
              : (0 as number);
            data.y.push(value);
          } else data.y.push(0);

          data.value.push(point.value[0]);
        });

        const convertedTimeUnit = timeUnitConverter(activeFreq);
        const filledDataObject = fillInTheGapsUtil(
          data,
          dateFrom,
          dateTo,
          convertedTimeUnit,
          settings?.timezone
        );
        (filledDataObject as IDataset).type = g.payment_type;

        datasetsPerType.push(filledDataObject as IDataset);
      });
      if (datasetsPerType?.length === 2)
        datasetsPerType = datasetsPerType.filter(
          (d) => d.type !== STATS.IPaymentType.ALL
        );
      setDatasets(datasetsPerType);
    }
  }, [graph, activeSummaryTab, activeFreq, dateFrom, dateTo]);

  useEffect(() => {
    Chart.register(verticalAnnotation);
  }, []);

  useEffect(() => {
    if (activeSummaryTab) {
      toFetchGraphPoints({
        start_date: dateFrom,
        end_date: dateTo,
        interval_types: intervalTypes.join(),
        payment_type: Object.values(STATS.IPaymentType).join(','),
      });
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    setActiveFreq(intervalTypes[0]);
  }, [dateFrom, dateTo]);

  const is_valid_datasets =
    datasets?.some(
      (dict) =>
        dict?.y && Array.isArray(dict?.y) && dict?.y?.some((value) => value > 0)
    ) || false;

  const overrideColorConfigForSingleChart = (arg: IDataset[]) => {
    if (arg?.length === 1) return customChartConfig[STATS.IPaymentType.ALL];
    return {};
  };

  return (
    <div
      id="dashboard-graph"
      className="shadow-card-hover rounded border-0 p-5 mb-7 bg-white"
    >
      <div className="flex justify-between mb-5">
        <SummaryWithComparison />
        <FrequencyButtonGroup
          enabledFreq={intervalTypes}
          activeFreq={activeFreq}
          setActiveFreq={setActiveFreq}
        />
      </div>
      {Array.isArray(graph) && graph?.length > 0 && is_valid_datasets ? (
        <>
          <div
            id="chartContainer"
            className="relative h-80"
          >
            <ChartReact
              type="line"
              options={getChartOptions(
                activeFreq,
                util.getTimeAxisStepSize(interval, activeFreq),
                util.getTimeAxisUnit(activeFreq),
                datasets.length,
                activeSummaryTab,
                datasets.map(({ value, type }) => ({ type, value })),
                settings?.timezone
              )}
              plugins={[htmlLegendPlugin]}
              data={{
                labels: datasets[0]?.x,
                datasets: datasets.map((d) => ({
                  data: d.y,
                  ...customChartConfig[d.type],
                  fill: true,
                  borderWidth: 2.5,
                  summary,
                  paymentType: d.type,
                  activeSummaryTab,
                  ...overrideColorConfigForSingleChart(datasets),
                })),
              }}
            />
          </div>
          <div id="legend-container" />
        </>
      ) : (
        <NoDataAvailable />
      )}
      <div className="flex justify-between mt-5 text-xs leading-4">
        <div className="flex justify-center text-gray-blue-50 font-normal">
          <GraphLastUpdatedTimer />
        </div>
        <Link
          className="text-dark-green-link text-sm font-medium leading-5"
          to={`../../${util.getRedirectPath(
            activeSummaryTab
          )}?tab=summary&dates=${dateFrom},${dateTo}`}
        >
          View transactions from this date range
        </Link>
      </div>
    </div>
  );
};

export default Graph;
